import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { processPDF } from "../onboardingUtils/pdfProcessor";
import { DrawerInitialStep } from "./DrawerInitialStep";
import { DrawerDetailsStep } from "./DrawerDetailsStep";
import { DrawerWorkflowStep } from "./DrawerWorkflowStep";
import Button from "../../Buttons/Button";

export const DocumentDrawer = ({
  open,
  step,
  multiple,
  documentForm,
  workflows,
  isProcessing,
  fileInputRef,
  documentType,
  onClose,
  onStepChange,
  onDocumentFormChange,
  onWorkflowsChange,
  onFileProcess,
  onProceed,
  onSkipWorkflow,
  setIsProcessing,
  workflowTargetDoc,
  hideApprovers = false,
}) => {
  const handleFileUpload = async (file) => {
    console.log({ file });
    if (!file) return;

    const fileType = file.name.split(".").pop().toLowerCase();
    if (!["pdf", "doc", "docx"].includes(fileType)) {
      alert("Only PDF, DOC, or DOCX files are supported");
      return;
    }

    if (fileType !== "pdf") {
      await onFileProcess(file);
      onStepChange("upload");
      return;
    }

    setIsProcessing(true);
    try {
      const tempId = `temp-${Date.now().toString()}`;
      const result = await processPDF(file, tempId);

      await onFileProcess(file, {
        ...result,
        tempId,
        isBlankDocument: false,
      });

      onStepChange("upload");
    } catch (error) {
      console.error("File processing error:", error);
      alert(`Error processing file: ${error.message || error}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case "initial":
        return "Add Document";
      case "upload":
        return "Document Details";
      case "workflow":
        return "Configure Workflow";
      default:
        return "Add Document";
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black z-40"
        onClick={onClose}
      />
      <motion.div
        key="drawer"
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "tween", duration: 0.3 }}
        className="fixed right-0 top-0 bottom-0 w-[500px] bg-white shadow-2xl z-50 flex flex-col overflow-hidden"
      >
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0 bg-white">
          <h2 className="text-xl font-semibold text-gray-900">
            {getStepTitle()}
          </h2>
          <Button
            type="icon"
            icon={"close"}
            variant="text"
            onClick={onClose}
            color="black"
          />
        </div>

        <div className="flex-1 overflow-y-auto p-6 bg-white">
          {step === "initial" && (
            <DrawerInitialStep
              fileInputRef={fileInputRef}
              onDesign={() => {
                onFileProcess(null, {
                  pages: [
                    {
                      number: 1,
                      image: "data:image/png;base64,...",
                      width: 816,
                      height: 1056,
                    },
                  ],
                  arrayBuffer: null,
                  pageCount: 1,
                  isBlankDocument: true,
                  tempId: `blank-${Date.now()}`,
                });
                onStepChange("upload");
              }}
              onUpload={() => fileInputRef.current?.click()}
              onFileChange={handleFileUpload}
              onStepChange={() => onStepChange("upload")}
            />
          )}

          {step === "upload" && (
            <DrawerDetailsStep
              documentForm={documentForm}
              setDocumentForm={onDocumentFormChange}
              documentType={documentType}
            />
          )}

          {step === "workflow" && (
            <DrawerWorkflowStep
              workflows={workflows}
              setWorkflows={onWorkflowsChange}
              multiple={multiple}
              hideApprovers={hideApprovers}
            />
          )}

          {!step && (
            <div className="text-center text-gray-500 py-8">
              <p>No step selected. Current step: {step}</p>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 px-6 py-4 flex justify-between bg-gray-50 flex-shrink-0">
          <Button title={"Cancel"} onClick={onClose} />
          <div className="flex gap-2">
            {step === "workflow" && !workflowTargetDoc && !hideApprovers && (
              <Button
                title={"Skip Workflow"}
                variant="outlined"
                color="black"
                onClick={onSkipWorkflow}
              />
            )}
            {step !== "initial" && (
              <Button
                onClick={onProceed}
                disabled={isProcessing}
                title={
                  isProcessing
                    ? "Processing..."
                    : workflowTargetDoc && step === "workflow"
                    ? "Save"
                    : "Proceed"
                }
              />
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
